import FilterSelect from '@sg/components/FilterSelect';
import { useRequestController } from '@sg/hooks/useRequestController';
import {
  LIVE_TITLE_COND_GLOBAL_CACHE_KEY,
  type ILiveTitleCondData,
} from '@sg/pages/StudioManagement/LiveTitles/hooks';
import {
  ALL_SYSTEMS_PLATFORM,
  buildPlatformRegionSelectOptions,
  hasExplicitPlatformTypes,
  parseLiveTitleOsOptions,
  resolveRegionSelectValue,
} from '@sg/pages/StudioManagement/LiveTitles/utils/liveTitleOsFilter';
import NewRegionSelect from '@/pages/home/components/NewRegionSelect';
import IconTipsCom from '@sg/components/IconTipsCom';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { isEmpty } from 'lodash-es';
import { useIntl } from 'umi';
import classNames from 'classnames';
import './index.scss';

export const LIVE_TITLE_PLATFORM_FILTER_CLASS = 'dbs-live-title-platform-filter';

interface ILivePlatFormFilterProps {
  gameTitle?: string | null;
  /** When provided, skips an internal cond fetch (parent already fetched). */
  liveTitleCondData?: ILiveTitleCondData | null;
  /** Parent owns the cond request; block child fetch even while data is loading. */
  sharedCondFetch?: boolean;
  /** Cond request loading state from parent (used with sharedCondFetch). */
  condLoading?: boolean;
  /**
   * Data Analytics: when Title is All / multi-select, still fetch platforms with
   * `live_title_studio_game: null` so the dropdown stays usable.
   */
  allowGlobalFallback?: boolean;
  value?: string[];
  onChange?: (value: string[]) => void;
  boxClassName?: string;
  className?: string;
  style?: React.CSSProperties;
}

const LivePlatFormFilter: FC<ILivePlatFormFilterProps> = ({
  gameTitle,
  liveTitleCondData,
  sharedCondFetch = false,
  condLoading = false,
  allowGlobalFallback = false,
  value,
  onChange,
  boxClassName,
  className,
}) => {
  const { locale, formatMessage } = useIntl();
  const [searchValue, setSearchValue] = useState('');
  // Global cond (null studio_game) when no single title is selected on Data Analytics.
  const isGlobalCond = !gameTitle && allowGlobalFallback;
  const canFetchCond = Boolean(gameTitle) || isGlobalCond;

  useEffect(() => {
    setSearchValue('');
  }, [gameTitle]);

  const condPayload = useMemo(
    () => ({
      live_title_country_code: null,
      live_title_os: null,
      live_title_studio_game: gameTitle ?? null,
    }),
    [gameTitle],
  );

  const { data: fetchedCondData, loading: internalCondLoading } = useRequestController({
    method: 'POST',
    url: '/api/v1/globalpedia_pc/cond',
    refreshDeps: condPayload,
    ready: canFetchCond && !sharedCondFetch,
    cacheKey: gameTitle
      ? `live-title-cond-${gameTitle}`
      : LIVE_TITLE_COND_GLOBAL_CACHE_KEY,
    // Global platform list changes rarely; game-scoped list must refetch on switch.
    staleTime: gameTitle ? 0 : 300000,
  });
  const data = liveTitleCondData ?? fetchedCondData;
  const isCondLoading = sharedCondFetch ? condLoading : internalCondLoading;
  const isDisabled = !canFetchCond || isCondLoading;

  const isGrouped = useMemo(
    () => hasExplicitPlatformTypes(data?.live_title_os),
    [data?.live_title_os],
  );

  const { options: filterSelectOptions, group } = useMemo(
    () => parseLiveTitleOsOptions(data?.live_title_os, {
      locale,
      searchValue,
    }),
    [data?.live_title_os, locale, searchValue],
  );

  const { options: cascadeOptions } = useMemo(
    () => buildPlatformRegionSelectOptions(data?.live_title_os, locale),
    [data?.live_title_os, locale],
  );

  const regionSelectValue = useMemo(
    () => resolveRegionSelectValue(value, cascadeOptions),
    [value, cascadeOptions],
  );

  const realRegionsList = useMemo(
    () => cascadeOptions.map((item) => item.value).filter(Boolean) as string[],
    [cascadeOptions],
  );

  const language = useMemo(() => ({
    cn: {
      button_region: formatMessage({ id: 'dbs_filter_platform' }),
      button_market: formatMessage({ id: 'dbs_filter_platform' }),
      region_title: '平台类型',
      radio_title: '聚合数据',
    },
    en: {
      button_region: formatMessage({ id: 'dbs_filter_platform' }),
      button_market: formatMessage({ id: 'dbs_filter_platform' }),
      region_title: 'Platform Type',
      radio_title: 'Aggregated Data',
    },
  }), [formatMessage]);

  const allInfo = 'Display data results based on de-duplicated users for all platforms';
  const radioOptions = useMemo(() => ([{
    value: ALL_SYSTEMS_PLATFORM,
    text: 'All',
    textCN: '全部',
    infoIcon: (
      <IconTipsCom
        tipString={allInfo}
        mWidth={314}
        style={{ width: 14, height: 14 }}
      />
    ),
  }]), []);

  const handleCascadeClose = useCallback((nextValue: {
    regions?: string[];
    markets?: string[];
    type?: string;
  } | null) => {
    const markets = nextValue?.markets || [];
    if (isEmpty(markets)) {
      onChange?.([ALL_SYSTEMS_PLATFORM]);
      return;
    }
    // Pass valueRef markets through (same reference as parent state when unchanged).
    onChange?.(markets);
  }, [onChange]);

  const triggerStyle = useMemo(() => ({
    marginRight: 10,
    ...(isDisabled ? { pointerEvents: 'none' as const, opacity: 0.6 } : {}),
  }), [isDisabled]);

  const filterKey = gameTitle ?? (isGlobalCond ? 'global' : 'none');

  // All `type` null → original FilterSelect (flat checkbox list, no grouping).
  if (!isGrouped) {
    return (
      <FilterSelect
        key={filterKey}
        locale={locale}
        name={formatMessage({ id: 'dbs_filter_platform' })}
        className={classNames(LIVE_TITLE_PLATFORM_FILTER_CLASS, className)}
        style={triggerStyle}
        tooltip={null}
        onClose={(nextValue: string[]) => {
          onChange?.(nextValue);
        }}
        onSearchValueChange={setSearchValue}
        defaultValue={[ALL_SYSTEMS_PLATFORM]}
        value={value || []}
        matchButtonWidth={false}
        clickClose={false}
        placeholder="All"
        closeOnScroll={false}
        positionFixed={false}
        disabled={isDisabled}
        radioOptions={[
          {
            value: ALL_SYSTEMS_PLATFORM,
            text: 'All',
            info: allInfo,
          },
        ]}
        options={filterSelectOptions}
        listWidth={271}
        boxClassName={classNames(
          'dbs-live-title-platform-filter-box',
          boxClassName,
        )}
      />
    );
  }

  return (
    <NewRegionSelect
      key={filterKey}
      className={classNames(LIVE_TITLE_PLATFORM_FILTER_CLASS, className)}
      boxClassName={classNames(
        'dbs-live-title-platform-filter-box',
        'dbc-region-select-container',
        boxClassName,
      )}
      locale={locale}
      language={language}
      value={regionSelectValue}
      options={cascadeOptions}
      radioOptions={radioOptions}
      realRegionsList={realRegionsList}
      shortcutOptions={[]}
      popularList={[]}
      selectFilterType="live_title_platform"
      closeOnScroll={false}
      isNotRegionIcon
      enableExactOverflowBubble
      rightListWidth={445}
      loading={isCondLoading}
      onCloseReginSelect={handleCascadeClose}
      {...({ style: triggerStyle } as Record<string, unknown>)}
    />
  );
};

export default LivePlatFormFilter;
